import ConfirmDialog from '@/components/ConfirmDialog';
import SortableHeader from '@/components/SortableHeader';
import TablePagination from '@/components/TablePagination';
import { useToast } from '@/hooks/useToast';
import { broadcastsService } from '@/services/broadcastsService';
import { CATEGORY_COLORS } from '@/types/templates';
import { formatDate as formatFullDate, truncateText } from '@/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BarChart2,
  CheckCircle,
  Clock,
  Filter,
  MessageSquare,
  Pause,
  Play,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Status badge component
 */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      label: 'Draft',
      icon: <Clock className="w-3 h-3 mr-1" />,
    },
    scheduled: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      label: 'Scheduled',
      icon: <Clock className="w-3 h-3 mr-1" />,
    },
    in_progress: {
      bg: 'bg-indigo-100',
      text: 'text-indigo-800',
      border: 'border-indigo-200',
      label: 'Sending',
      icon: <Play className="w-3 h-3 mr-1 animate-pulse" />,
    },
    completed: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      border: 'border-emerald-200',
      label: 'Completed',
      icon: <CheckCircle className="w-3 h-3 mr-1" />,
    },
    failed: {
      bg: 'bg-rose-100',
      text: 'text-rose-800',
      border: 'border-rose-200',
      label: 'Failed',
      icon: <AlertCircle className="w-3 h-3 mr-1" />,
    },
    cancelled: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      label: 'Cancelled',
      icon: <Trash2 className="w-3 h-3 mr-1" />,
    },
    paused: {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      label: 'Paused',
      icon: <Pause className="w-3 h-3 mr-1" />,
    },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text} ${config.border} shadow-sm border border-black/5`}
    >
      {config.icon}
      {config.label}
    </span>
  );
};

/**
 * Analytics summary component
 */
const AnalyticsSummary = ({ statistics, recipientCount }) => {
  if (!statistics) return <span className="text-gray-400 text-xs">-</span>;

  const { sent = 0, delivered = 0, read = 0, failed = 0 } = statistics;

  // Calculate percentages
  const deliveredRate =
    sent > 0 ? Math.round((delivered / recipientCount) * 100) : 0;

  return (
    <div className="flex flex-col space-y-1.5 min-w-[140px]">
      <div className="flex justify-between items-center text-[10px] uppercase tracking-wider font-bold text-gray-500">
        <span>Delivery</span>
        <span className="text-gray-900">{deliveredRate}%</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
        <div
          className="bg-emerald-500 h-full rounded-full transition-all duration-500"
          style={{ width: `${deliveredRate}%` }}
        />
      </div>
      <div className="flex justify-between items-center text-[10px] mt-1">
        <div className="flex space-x-2">
          <span className="text-gray-600" title="Sent">
            <span className="font-bold">{sent}</span> S
          </span>
          <span className="text-emerald-700" title="Delivered">
            <span className="font-bold">{delivered}</span> D
          </span>
          <span className="text-blue-700" title="Read">
            <span className="font-bold">{read}</span> R
          </span>
          <span className="text-rose-700" title="Failed">
            <span className="font-bold">{failed}</span> F
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * BroadcastsList page component
 */
const BroadcastsList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // State
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [broadcastToDelete, setBroadcastToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Handle sorting
  const handleSort = field => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Fetch broadcasts
  const {
    data: broadcastsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'broadcasts',
      projectId,
      page,
      limit,
      search,
      statusFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () => {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      return broadcastsService.getBroadcasts(projectId, params);
    },
    enabled: !!projectId,
    staleTime: 30000, // 30 seconds
  });

  const broadcasts = broadcastsData?.docs || [];
  const totalPages = broadcastsData?.totalPages || 0;
  const totalDocs = broadcastsData?.totalDocs || 0;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: broadcastId => broadcastsService.deleteBroadcast(broadcastId),
    onSuccess: () => {
      showSuccess('Broadcast deleted successfully');
      queryClient.invalidateQueries(['broadcasts', projectId]);
      setShowDeleteDialog(false);
      setBroadcastToDelete(null);
    },
    onError: error => {
      showError(error.response?.data?.message || 'Failed to delete broadcast');
    },
  });

  // Handle delete click
  const handleDeleteClick = broadcast => {
    setBroadcastToDelete(broadcast);
    setShowDeleteDialog(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (broadcastToDelete) {
      deleteMutation.mutate(broadcastToDelete._id);
    }
  };

  // Navigate to detail page
  const handleBroadcastClick = broadcastId => {
    navigate(`/projects/${projectId}/broadcasts/${broadcastId}`);
  };

  return (
    <div className="h-full bg-gray-50 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white shadow flex-shrink-0">
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center">
                <BarChart2 className="w-8 h-8 mr-3 text-primary-600" />
                Broadcasts
              </h1>
              <p className="mt-1 text-sm text-gray-500 font-medium">
                Manage and monitor your WhatsApp broadcast campaigns (
                {totalDocs} total)
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                type="button"
                onClick={() =>
                  navigate(`/projects/${projectId}/broadcasts/create`)
                }
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Plus className="-ml-1 mr-2 h-5 w-5" />
                Create Broadcast
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                name="search"
                id="search"
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search campaigns by name..."
              />
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-56 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
              <select
                id="status-filter"
                name="status-filter"
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm appearance-none"
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table Container area */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#fcfcfd] sticky top-0 z-10 shadow-sm">
              <tr>
                <SortableHeader
                  columnId="name"
                  label="Broadcast"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="templateName"
                  label="Template"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest bg-gray-50"
                >
                  Category
                </th>
                <SortableHeader
                  columnId="status"
                  label="Status"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="recipientCount"
                  label="Targeting"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-widest bg-gray-50"
                >
                  Analytics
                </th>
                <SortableHeader
                  columnId="totalCostIncurred"
                  label="Total Cost"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="createdAt"
                  label="Created At"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  columnId="completedAt"
                  label="Completed At"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <th scope="col" className="relative py-3 px-6 bg-gray-50">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      <span className="mt-2 text-sm">Loading campaigns...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-red-600 text-sm"
                  >
                    Error loading broadcasts. Please try again.
                  </td>
                </tr>
              ) : broadcasts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center">
                      <BarChart2 className="w-12 h-12 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">
                        No broadcasts found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {search || statusFilter !== 'all'
                          ? 'Try adjusting your search or filters.'
                          : 'Get started by creating your first broadcast campaign.'}
                      </p>
                      {!search && statusFilter === 'all' && (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/projects/${projectId}/broadcasts/create`)
                          }
                          className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Create Broadcast
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                broadcasts.map(broadcast => (
                  <tr
                    key={broadcast._id}
                    className="hover:bg-gray-50/80 transition-colors group cursor-pointer"
                    onClick={() => handleBroadcastClick(broadcast._id)}
                  >
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                          {broadcast.name}
                        </span>
                        <div className="flex items-center mt-1 space-x-2">
                          {broadcast.description && (
                            <span className="text-gray-400 text-[10px] bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
                              {truncateText(broadcast.description, 30)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="flex flex-col">
                        <div className="flex items-center text-sm font-medium text-gray-700">
                          <MessageSquare className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          {broadcast.templateName || 'Unknown'}
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1 uppercase font-bold tracking-tighter">
                          {broadcast.templateLanguage || 'en_US'}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      {broadcast.template?.category ? (
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${
                            CATEGORY_COLORS[broadcast.template.category] ||
                            'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {broadcast.template.category.charAt(0).toUpperCase() +
                            broadcast.template.category.slice(1).toLowerCase()}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <StatusBadge status={broadcast.status} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="flex flex-col">
                        <div className="flex items-center text-sm text-gray-900 font-bold">
                          <Users className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                          {broadcast.recipientCount?.toLocaleString() || 0}
                        </div>
                        <span className="text-[10px] text-gray-500 mt-1 capitalize font-medium">
                          via {broadcast.contactSelectionMethod || 'unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <AnalyticsSummary
                        statistics={broadcast.statistics}
                        recipientCount={broadcast.recipientCount}
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="text-sm text-gray-900 font-medium">
                        {`₹ ${broadcast.totalCostIncurred?.toFixed(2) || 0}`}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="text-sm text-gray-900 font-medium">
                        {formatFullDate(broadcast.createdAt)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-5">
                      <div className="text-sm text-gray-900 font-medium">
                        {broadcast.completedAt
                          ? formatFullDate(broadcast.completedAt)
                          : '-'}
                      </div>
                    </td>
                    <td className="relative whitespace-nowrap px-6 py-5 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        {broadcast.status === 'scheduled' && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteClick(broadcast);
                            }}
                            className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Standard Pagination */}
      <TablePagination
        pagination={{
          pageIndex: page - 1,
          pageSize: limit,
          total: totalDocs,
          totalPages: totalPages,
          hasPrevPage: page > 1,
          hasNextPage: page < totalPages,
        }}
        onPageChange={pageIndex => setPage(pageIndex + 1)}
        onPageSizeChange={pageSize => {
          setLimit(pageSize);
          setPage(1);
        }}
      />

      {/* Modals & Dialogs */}

      {showDeleteDialog && (
        <ConfirmDialog
          title="Delete Broadcast"
          message={`Are you sure you want to delete "${broadcastToDelete?.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteDialog(false);
            setBroadcastToDelete(null);
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default BroadcastsList;
